import React, { useState } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Avatar, 
  Modal, 
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Typography
} from 'antd';
import { 
  CheckOutlined, 
  CloseOutlined, 
  EyeOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { MentorBookingResponse } from '../../../types';

const { Title, Text } = Typography;

const OneOnOneMeetingsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const { data: bookings = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.bookings,
    queryFn: async () => {
      const res = await apiService.getMentorBookings();
      if (!res.success) throw new Error(res.message || 'Randevular yüklenirken hata oluştu');
      return res.data;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ bookingId, status }: { bookingId: number; status: string }) =>
      apiService.updateBookingStatus(bookingId, status),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Randevu güncellendi');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bookings });
      } else toast.error(res.message || 'Randevu güncellenirken hata oluştu');
    },
    onError: () => toast.error('Randevu güncellenirken hata oluştu'),
  });

  const completeMutation = useMutation({
    mutationFn: (bookingId: number) => apiService.completeBooking(bookingId),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Randevu tamamlandı');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bookings });
      } else toast.error(res.message || 'Randevu tamamlanırken hata oluştu');
    },
    onError: () => toast.error('Randevu tamamlanırken hata oluştu'),
  });

  const handleApprove = (bookingId: number) => updateStatusMutation.mutate({ bookingId, status: 'Confirmed' });
  const handleReject = (bookingId: number) => updateStatusMutation.mutate({ bookingId, status: 'Cancelled' });
  const handleComplete = (bookingId: number) => completeMutation.mutate(bookingId);

  const handleViewDetails = (booking: any) => {
    setSelectedBooking(booking);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Confirmed': return 'blue';
      case 'Cancelled': return 'red';
      case 'Completed': return 'green';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending': return 'Beklemede';
      case 'Confirmed': return 'Onaylandı';
      case 'Cancelled': return 'İptal Edildi';
      case 'Completed': return 'Tamamlandı';
      default: return status;
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: 'Mentee',
      key: 'mentee',
      width: 200,
      render: (record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40} 
            src={record.mentee.profileImage ? `data:image/jpeg;base64,${record.mentee.profileImage}` : undefined}
            icon={<UserOutlined />}
            style={{ backgroundColor: 'var(--primary-500)', marginRight: '12px' }}
          />
          <div>
            <div style={{ fontWeight: '500' }}>
              {record.mentee.firstName} {record.mentee.lastName}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.mentee.email}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Tarih & Saat',
      key: 'datetime',
      width: 180,
      render: (record: any) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <CalendarOutlined style={{ marginRight: '4px', color: 'var(--primary-500)' }} />
            <Text strong>{formatDateTime(record.startTime).split(' ')[0]}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ClockCircleOutlined style={{ marginRight: '4px', color: 'var(--success)' }} />
            <Text>{formatDateTime(record.startTime).split(' ')[1]} - {formatDateTime(record.endTime).split(' ')[1]}</Text>
          </div>
        </div>
      )
    },
    {
      title: 'Hizmet',
      dataIndex: 'mentorService',
      key: 'service',
      width: 150,
      render: (service: any) => (
        <div>
          <div style={{ fontWeight: '500' }}>{service?.title || 'Genel Görüşme'}</div>
          {service?.description && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {service.description.length > 30 
                ? `${service.description.substring(0, 30)}...` 
                : service.description
              }
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Ücret',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price: number) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <DollarOutlined style={{ marginRight: '4px', color: 'var(--success)' }} />
          <Text strong>${price}</Text>
        </div>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (record: any) => (
        <Space>
          <Tooltip title="Detayları Görüntüle">
            <Button 
              type="text" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {record.status === 'Pending' && (
            <>
              <Tooltip title="Onayla">
                <Button 
                  type="text" 
                  icon={<CheckOutlined />}
                  style={{ color: 'var(--success)' }}
                  onClick={() => handleApprove(record.id)}
                />
              </Tooltip>
              <Tooltip title="Reddet">
                <Button 
                  type="text" 
                  icon={<CloseOutlined />}
                  style={{ color: 'var(--error)' }}
                  onClick={() => handleReject(record.id)}
                />
              </Tooltip>
            </>
          )}
          {record.status === 'Confirmed' && (
            <Tooltip title="Tamamlandı Olarak İşaretle">
              <Button 
                type="text" 
                icon={<CheckOutlined />}
                style={{ color: 'var(--primary-500)' }}
                onClick={() => handleComplete(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  // İstatistikler
  const totalBookings = bookings.length;
  const pendingBookings = bookings.filter(b => b.status === 'Pending').length;
  const confirmedBookings = bookings.filter(b => b.status === 'Confirmed').length;
  const completedBookings = bookings.filter(b => b.status === 'Completed').length;
  const totalRevenue = bookings
    .filter(b => b.status === 'Completed')
    .reduce((sum, b) => sum + b.price, 0);

  return (
    <div>
      {/* İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Randevu"
              value={totalBookings}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: 'var(--primary-500)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bekleyen"
              value={pendingBookings}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Onaylanan"
              value={confirmedBookings}
              prefix={<CheckOutlined />}
              valueStyle={{ color: 'var(--success)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          
        </Col>
      </Row>

      {/* Randevu Listesi */}
      <TabListLoading loading={loading}>
      <Card title="1-1 Görüşmelerim">
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={bookings}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} randevu`
          }}
        />
      </Card>
      </TabListLoading>

      {/* Detay Modal */}
      <Modal
        title="Randevu Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedBooking && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <Avatar 
                    size={60} 
                    src={selectedBooking.mentee.profileImage ? `data:image/jpeg;base64,${selectedBooking.mentee.profileImage}` : undefined}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: 'var(--primary-500)', marginRight: '16px' }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedBooking.mentee.firstName} {selectedBooking.mentee.lastName}
                    </Title>
                    <Text type="secondary">{selectedBooking.mentee.email}</Text>
                  </div>
                </div>
              </Col>
              
              <Col span={12}>
                <Text strong>Başlangıç:</Text>
                <br />
                <Text>{formatDateTime(selectedBooking.startTime)}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Bitiş:</Text>
                <br />
                <Text>{formatDateTime(selectedBooking.endTime)}</Text>
              </Col>
              
              <Col span={12}>
                <Text strong>Durum:</Text>
                <br />
                <Tag color={getStatusColor(selectedBooking.status)}>
                  {getStatusText(selectedBooking.status)}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong>Ücret:</Text>
                <br />
                <Text strong style={{ color: 'var(--success)' }}>${selectedBooking.price}</Text>
              </Col>
              
              {selectedBooking.mentorService && (
                <Col span={24}>
                  <Text strong>Hizmet:</Text>
                  <br />
                  <Text>{selectedBooking.mentorService.title}</Text>
                  {selectedBooking.mentorService.description && (
                    <>
                      <br />
                      <Text type="secondary">{selectedBooking.mentorService.description}</Text>
                    </>
                  )}
                </Col>
              )}
              
              {selectedBooking.notes && (
                <Col span={24}>
                  <Text strong>Notlar:</Text>
                  <br />
                  <Text>{selectedBooking.notes}</Text>
                </Col>
              )}
              
              {selectedBooking.meetingLink && (
                <Col span={24}>
                  <Text strong>Toplantı Linki:</Text>
                  <br />
                  <Button 
                    type="link" 
                    icon={<VideoCameraOutlined />}
                    href={selectedBooking.meetingLink}
                    target="_blank"
                  >
                    Toplantıya Katıl
                  </Button>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OneOnOneMeetingsTab;
